<template>
    <button
        role="tab"
        :aria-selected="isActive"
        class="relative w-full text-left flex flex-col"
        :class="{ 'cursor-not-allowed': state === 'locked', 'cursor-pointer': state !== 'locked', 'opacity-40': isDimmed, 'hover:opacity-60': isDimmed }"
        :disabled="state === 'locked'"
        @click="handleClick">
        <div class="flex flex-col grow items-center text-center gap-1.5 py-3 sm:flex-row sm:items-center sm:text-left sm:gap-3">
            <!-- Icon -->
            <div class="shrink-0 rounded-full flex items-center justify-center transition-colors h-7 w-7 bg-[#9596FF]">
                <Checkmark v-if="state === 'completed'" width="14" height="14" class="'text-black'" />
                <Clock v-else-if="state === 'open'" width="14" height="14" class="'text-black'" />
                <Archive v-else-if="state === 'expired'" width="14" height="14" class="'text-black'" />
                <LockSmall v-else width="14" height="14" class="text-black" />
            </div>

            <div class="min-w-0 w-full sm:w-auto grow flex flex-col justify-between self-stretch">
                <!-- Moment title text -->
                <div class="details sm:body-small-bold text-gray-800 line-clamp-2 sm:line-clamp-1">{{ momentTitle }}{{ isPreview ? " (preview)" : "" }}</div>
                <!-- Date and state text -->
                <!-- FIXME: one off font weight change -->
                <div class="details text-gray-600 font-medium mt-0.5 line-clamp-2 sm:line-clamp-1">
                    <span class="inline-block sm:inline">{{ date ? `${date}` : "" }}</span>
                    <span class="hidden sm:inline"> · </span>
                    <span class="font-normal sm:font-medium mt-0.5 sm:mt-0 block sm:inline">{{ stateDisplay }}</span>
                </div>
            </div>
        </div>

        <!-- Underline indicator: purple for the selected tab, muted track otherwise -->
        <div class="absolute -bottom-0.5 left-0 right-0 h-1 rounded-full" :class="isActive ? 'bg-[#9596FF]' : 'bg-gray-200'"></div>
    </button>
</template>

<script setup lang="ts">
import Archive from "~vue/icons/Archive.vue";
import Checkmark from "~vue/icons/Checkmark.vue";
import Clock from "~vue/icons/Clock.vue";
import LockSmall from "~vue/icons/LockSmall.vue";
import type { PerformanceMomentState } from "~vue/types/performance_management";
import { computed } from "vue";

interface Props {
    state: PerformanceMomentState;
    date?: string;
    isActive?: boolean;
    momentId: number;
    momentTitle: string;
    isPreview?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    date: "",
    isActive: false,
});

const emit = defineEmits<{
    activate: [momentId: number];
}>();

// In-progress ("open") moments must never look greyed out, even when another tab is
// selected — orgs can run multiple active moments at once (ENG-1330). Only inactive
// states (locked/completed/expired) are dimmed when unselected.
const isDimmed = computed(() => !props.isActive && props.state !== "open");

const handleClick = () => {
    // Don't allow activating locked moments
    if (props.state === "locked") {
        return;
    }

    emit("activate", props.momentId);
};

const stateDisplay = computed(() => {
    switch (props.state) {
        case "locked":
            return "Closed";
        case "open":
            return "In Progress";
        case "completed":
            return "Completed";
        case "expired":
            return "Expired";
        default:
            return "";
    }
});
</script>

<style scoped>
.bg-open {
    background: linear-gradient(135deg, rgba(158, 139, 191, 0.9) 0%, rgba(119, 107, 211, 1) 20%, rgba(119, 107, 211, 1) 50%, rgba(187, 157, 207, 0.8) 100%);
}

.bg-completed {
    border: 1px solid #c7f0a7;
    background: #f6ffed;
    color: #58a942;
}
</style>
